// src/pages/Search/SearchSummaryList.jsx
import React, { useCallback } from 'react'
import { Box, Typography, Stack } from '@mui/material'
import { useIsMobile } from '@/hooks/Responsive'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useNavigate } from 'react-router-dom'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { cleanSearchHtml } from '@/api/util'

export default function SearchSummaryList({ summary, TAB_MAP, keyword }) {
  if (!summary) return null

  return (
    <Box>
      <Stack spacing={2} mt={2}>
        {Object.entries(TAB_MAP).map(([key, meta]) =>
          key !== 'all' ? (
            <SummaryInlineCard
              key={key}
              title={meta.title}
              count={summary[meta.countKey]}
              preview={summary[meta.previewKey]}
              keyword={keyword}
            />
          ) : null
        )}
      </Stack>
    </Box>
  )
}

function SummaryInlineCard({ title, count, preview, keyword }) {
  const { isMobile } = useIsMobile()
  const navigate = useNavigate()
  const lang = useLang()

  const onItemClick = useCallback(
    (row) => {
      const tab = row?.tab

      if (tab === 'course') {
        navigate(buildLangHref(`/contents/lectureview/${row.id}`, lang), { state: { row } })
        return
      }

      if (tab === 'xr') {
        navigate(buildLangHref(`/contents/xrview/${row.id}`, lang), { state: { row } })
        return
      }

      if (tab === 'notice') {
        navigate('/alarm/noticeview', {
          state: {
            searchYn: 'Y',
            navParamData: { page: 1, pageSize: 10, recordSize: 10, searchNm: keyword || '' },
            rowData: { bbsId: row.id },
          },
        })
        return
      }

      if (tab === 'faq') {
        navigate('/alarm/faq', {
          state: {
            activeTab: row?.faqCategory ?? '',
            openId: row?.id,
          },
        })
      }
    },
    [navigate, lang, keyword]
  )

  return (
    <Box>
      {preview?.length > 0 ? (
        <>
          <Typography
            variant={isMobile ? 'subtitle2' : 'subtitle1'}
            fontWeight={700}
            sx={{ mb: 3 }}
          >
            {title}{' '}
            <Typography
              component="span"
              variant={isMobile ? 'subtitle2' : 'subtitle1'}
              color="primary.main"
              fontWeight={700}
            >
              {count}
            </Typography>
            건
          </Typography>

          <Stack spacing={3} sx={{ mb: 8 }}>
            {preview.slice(0, 3).map((item) => (
              <Stack
                key={`${item.tab}-${item.id}`}
                tabIndex={0}
                role="button"
                spacing={1.5}
                onClick={() => onItemClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onItemClick(item)
                  }
                }}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 4,
                  px: 6,
                  py: 4,
                  color: 'text.primary',
                  cursor: 'pointer',
                  outline: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus-visible': { boxShadow: '0 0 0 2px rgba(0,0,0,0.25)' },
                }}
              >
                <Typography
                  variant={isMobile ? 'subtitle3' : 'subtitle2'}
                  fontWeight={500}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  dangerouslySetInnerHTML={{ __html: cleanSearchHtml(item.description) }}
                >
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      ) : (
        <>
          <Typography
            variant={isMobile ? 'subtitle2' : 'subtitle1'}
            fontWeight={700}
            sx={{ mb: 3 }}
          >
            {title} 0건
          </Typography>

          <Stack
            alignItems="center"
            spacing={1.5}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 4,
              px: 6,
              py: 4,
              mb: 8,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
            <Typography color="grey.500">검색결과가 없습니다.</Typography>
          </Stack>
        </>
      )}
    </Box>
  )
}
